// ============================================================
// fondos/efectos/neural.js — «Neural»: nodos que derivan y se unen con líneas (efecto de LIENZO)
//
// Portado de `vendor/bg/neural-bg.js` en la F2b del P3 con el MISMO dibujo: mismos nodos, mismas
// distancias, mismos alfas y el mismo orden de azar (el test de espejo lo compara llamada a llamada
// con la clase de siempre). Lo nuevo es de la técnica, no del efecto: la velocidad va por `dt`
// (`comun.js::PASO`) y el puntero llega desde la SECCIÓN (`fondos/lienzo.js`), así que las líneas
// hacia el ratón por fin se ven (E12: la capa es `pointer-events:none` y el canvas nunca las oía).
//
// F3 (E13): los alfas salen del tope del catálogo (`alfaMax`, el de los nodos) y sobre CLARO los
// nodos y las uniones crecen (`comun.js::TAM_CLARO`): sobre claro casi no se veían. En oscuro, el
// dibujo de siempre.
// ============================================================

import { pasos, rgbDe, interpolar, tamano } from './comun.js';

/** Distancia (px CSS) a la que dos nodos se unen, y a la que un nodo se une al puntero. */
const DISTANCIA = 120;
const DISTANCIA_PUNTERO = 180;

/** Alfa de una unión a distancia 0, respecto al de los nodos (el tope). */
const LINEA = 0.85;

export default {
    crear(ctx, e) {
        const p = e.params;
        // Velocidad 1-10 → 0,4×-3×; sin dato, 1× (NO el 5 del catálogo, que daría 1,56×).
        const speedMult = p.speed != null ? (0.4 + (p.speed - 1) * (2.6 / 9)) : 1.0;
        // Densidad 1-10 → 15-80 nodos (10-45 en táctil).
        const n = Math.round(interpolar(p.density, 5, e.tactil ? 10 : 15, e.tactil ? 45 : 80));
        const nodos = [];
        for (let i = 0; i < n; i++) {
            nodos.push({
                x: e.azar(),
                y: e.azar(),
                vx: (e.azar() - 0.5) * 0.5 * speedMult,
                vy: (e.azar() - 0.5) * 0.5 * speedMult,
            });
        }
        return { w: e.w, h: e.h, rgb: rgbDe(e.c1), alfa: e.alfa, tam: tamano(e.oscuro), nodos: nodos };
    },

    redimensionar(s, w, h) {
        s.w = w;
        s.h = h;
    },

    dibujar(s, ctx, t, dt, puntero) {
        const k = pasos(dt);
        const rgb = s.rgb;
        const W = s.w;
        const H = s.h;
        const distSq = DISTANCIA * DISTANCIA;
        const punteroSq = DISTANCIA_PUNTERO * DISTANCIA_PUNTERO;

        // Un fotograma quieto (dt 0) repinta el estado tal cual: sin mover ni rebotar nada.
        if (k > 0) {
            s.nodos.forEach(function (node) {
                node.x += node.vx * k / W;
                node.y += node.vy * k / H;
                if (node.x <= 0 || node.x >= 1) node.vx *= -1;
                if (node.y <= 0 || node.y >= 1) node.vy *= -1;
                node.x = Math.max(0, Math.min(1, node.x));
                node.y = Math.max(0, Math.min(1, node.y));
            });
        }

        const a = s.alfa;
        ctx.lineWidth = s.tam;
        for (let i = 0; i < s.nodos.length; i++) {
            const n1 = s.nodos[i];
            const px1 = n1.x * W;
            const py1 = n1.y * H;

            if (puntero) {
                const mdx = px1 - puntero.x, mdy = py1 - puntero.y;
                const dPunteroSq = mdx * mdx + mdy * mdy;
                if (dPunteroSq < punteroSq) {
                    const dPuntero = Math.sqrt(dPunteroSq);
                    const alpha = Math.min(a, (1 - dPuntero / DISTANCIA_PUNTERO) * a * LINEA * 1.5);
                    ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
                    ctx.beginPath();
                    ctx.moveTo(px1, py1);
                    ctx.lineTo(puntero.x, puntero.y);
                    ctx.stroke();
                }
            }

            for (let j = i + 1; j < s.nodos.length; j++) {
                const n2 = s.nodos[j];
                const px2 = n2.x * W;
                const py2 = n2.y * H;
                const dx = px1 - px2, dy = py1 - py2;
                const dSq = dx * dx + dy * dy;
                if (dSq < distSq) {
                    const d = Math.sqrt(dSq);
                    const alpha = (1 - d / DISTANCIA) * a * LINEA;
                    ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
                    ctx.beginPath();
                    ctx.moveTo(px1, py1);
                    ctx.lineTo(px2, py2);
                    ctx.stroke();
                }
            }
        }

        s.nodos.forEach(function (node) {
            ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
            ctx.beginPath();
            ctx.arc(node.x * W, node.y * H, 2 * s.tam, 0, Math.PI * 2);
            ctx.fill();
        });
    },
};
